import { useState, type FormEvent } from 'react';
import { ShieldAlert } from 'lucide-react';

import { track } from '../lib/analytics';
import { api } from '../lib/api';
import { useAction } from '../lib/hooks';
import { useApiOrigin } from '../lib/plane-config';
import type { CreatedKey } from '../lib/types';
import { apiUrlLine } from './code';
import { CopyAction, CopyButton, ErrorCallout, Spinner } from './ui';

/**
 * The secret, shown exactly once, with the shell line an agent needs.
 * `bare` drops the box and the title, for a dialog that has its own.
 */
export function KeyReveal({ created, onDone, bare = false }: { created: CreatedKey; onDone?: () => void; bare?: boolean }) {
  const exportLine = `export OFFSTAGE_API_KEY=${created.key}`;
  const urlLine = apiUrlLine(useApiOrigin());
  const both = `${exportLine}\n${urlLine}`;
  return (
    <div className={`reveal${bare ? ' reveal--bare' : ''}`} role="status" aria-live="polite">
      {bare ? null : (
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <p className="h3" style={{ margin: 0 }}>
            Key “{created.name}” is ready
          </p>
          {onDone ? (
            <button type="button" className="btn btn--sm btn--quiet" onClick={onDone}>
              Done
            </button>
          ) : null}
        </div>
      )}
      <div className="reveal__row">
        <code>{created.key}</code>
        <CopyButton text={created.key} label="Copy key" />
      </div>
      <div className="reveal__row">
        <code>{exportLine}</code>
        <CopyButton text={exportLine} label="Copy key export line" />
      </div>
      <div className="reveal__row">
        <code>{urlLine}</code>
        <CopyButton text={urlLine} label="Copy API URL export line" />
      </div>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <p className="reveal__note">
          <ShieldAlert aria-hidden="true" />
          Shown once. Copy it now; you can always create another key.
        </p>
        <div className="row" style={{ gap: 8 }}>
          {bare && onDone ? (
            <button type="button" className="btn btn--sm" onClick={onDone}>
              Done
            </button>
          ) : null}
          <CopyAction text={both} label="Copy both lines" size="sm" primary />
        </div>
      </div>
    </div>
  );
}

export function KeyCreateForm({ onCreated, defaultName = '', submitLabel = 'Create key', autoFocus = false }: { onCreated: (key: CreatedKey) => void; defaultName?: string; submitLabel?: string; autoFocus?: boolean }) {
  const [name, setName] = useState(defaultName);
  const create = useAction(async (keyName: string) => api<CreatedKey>('/v1/keys', { method: 'POST', body: { name: keyName } }));

  async function submit(e: FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    const created = await create.run(trimmed);
    if (created) {
      track('api_key_created');
      onCreated(created);
      setName('');
    }
  }

  return (
    <div className="stack">
      <form className="inline-form" onSubmit={submit}>
        <div className="field">
          <label htmlFor="key-name">Key name</label>
          <input
            id="key-name"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="claude-code, ci, my-laptop"
            maxLength={64}
            autoComplete="off"
            spellCheck={false}
            autoFocus={autoFocus}
          />
        </div>
        <button type="submit" className="btn btn--primary" disabled={!name.trim() || create.busy} aria-busy={create.busy} style={{ minHeight: 38 }}>
          {create.busy ? <Spinner /> : null}
          {submitLabel}
        </button>
      </form>
      {create.error ? <ErrorCallout error={create.error} /> : null}
    </div>
  );
}
